import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { AsyncStatus } from "../../components/types";
import { useSocketAck } from "../../components/hooks/use-socket-ack.hook";
import { useParams } from "react-router-dom";
import { safeAtob } from "src/shared/helpers/enode-decode-string";

export type ZoneConfigurationAckState = {
  status: AsyncStatus;
  countdown: number;
};

type ZoneConfigurationAckContextValue = {
  states: Record<number, ZoneConfigurationAckState>;
  historyRefetchTriggers: Record<number, number>;
  start: (zoneId: number) => void;
  fail: (zoneId: number) => void;
  setHistoryOpenZoneId: (zoneId: number | null) => void;
};

const ZoneConfigurationAckContext = createContext<ZoneConfigurationAckContextValue | null>(null);

type ZoneConfigurationAckProviderProps = {
  children: ReactNode;
};

export function ZoneConfigurationAckProvider({ children }: ZoneConfigurationAckProviderProps) {
  const [states, setStates] = useState<Record<number, ZoneConfigurationAckState>>({});
  const [historyRefetchTriggers, setHistoryRefetchTriggers] = useState<Record<number, number>>({});
  const pendingZoneIdsRef = useRef<Set<number>>(new Set());
  const deadlinesRef = useRef<Record<number, number>>({});
  const resultTimersRef = useRef<Record<number, number>>({});
  const historyOpenZoneIdRef = useRef<number | null>(null);

  const params: any = useParams();
  const assetCode = params?.id ? safeAtob(params?.id) : null;

  const finish = useCallback((zoneId: number, status: "success" | "failed") => {
    if (!pendingZoneIdsRef.current.has(zoneId)) return;

    pendingZoneIdsRef.current.delete(zoneId);
    delete deadlinesRef.current[zoneId];
    setStates((current) => ({
      ...current,
      [zoneId]: { status, countdown: 0 },
    }));
  }, []);

  const start = useCallback((zoneId: number) => {
    const existingResultTimer = resultTimersRef.current[zoneId];
    if (existingResultTimer) {
      window.clearTimeout(existingResultTimer);
      delete resultTimersRef.current[zoneId];
    }

    pendingZoneIdsRef.current.add(zoneId);
    deadlinesRef.current[zoneId] = Date.now() + 5 * 60 * 1000;
    setStates((current) => ({
      ...current,
      [zoneId]: { status: "pending", countdown: 300 },
    }));
  }, []);

  const fail = useCallback((zoneId: number) => finish(zoneId, "failed"), [finish]);
  const setHistoryOpenZoneId = useCallback((zoneId: number | null) => {
    historyOpenZoneIdRef.current = zoneId;
  }, []);

  const pendingZoneIdsKey = Object.keys(states)
    .filter((zoneId) => states[Number(zoneId)].status === "pending")
    .join(",");

  useEffect(() => {
    if (!pendingZoneIdsKey) return;

    const interval = window.setInterval(() => {
      const now = Date.now();
      const expiredZoneIds = Object.entries(deadlinesRef.current)
        .filter(([, deadline]) => deadline <= now)
        .map(([zoneId]) => Number(zoneId));

      expiredZoneIds.forEach((zoneId) => {
        pendingZoneIdsRef.current.delete(zoneId);
        delete deadlinesRef.current[zoneId];
      });

      setStates((current) => {
        let next = current;
        Object.entries(current).forEach(([zoneId, state]) => {
          if (state.status !== "pending") return;
          const numericZoneId = Number(zoneId);
          const remainingSeconds = Math.max(
            0,
            Math.ceil(((deadlinesRef.current[numericZoneId] ?? now) - now) / 1000),
          );
          next = {
            ...next,
            [numericZoneId]: expiredZoneIds.includes(numericZoneId)
              ? { status: "failed", countdown: 0 }
              : { ...state, countdown: remainingSeconds },
          };
        });
        return next;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [pendingZoneIdsKey]);

  useEffect(() => {
    Object.entries(states).forEach(([zoneId, state]) => {
      const numericZoneId = Number(zoneId);
      if (state.status === "pending" || resultTimersRef.current[numericZoneId]) return;

      resultTimersRef.current[numericZoneId] = window.setTimeout(() => {
        setStates((current) => {
          if (current[numericZoneId]?.status !== state.status) return current;
          const next = { ...current };
          delete next[numericZoneId];
          return next;
        });
        delete resultTimersRef.current[numericZoneId];
      }, 3000);
    });
  }, [states]);

  useEffect(() => () => {
    Object.values(resultTimersRef.current).forEach((timer) => window.clearTimeout(timer));
  }, []);

  const handleAck = useCallback((socketData: any) => {
    const rawZoneId = socketData?.Value;
    if (rawZoneId == null || String(rawZoneId).trim() === "") return;

    const zoneId = Number(rawZoneId);
    if (!Number.isSafeInteger(zoneId)) return;

    if (pendingZoneIdsRef.current.has(zoneId)) {
      finish(
        zoneId,
        String(socketData?.ToasterColor).toLowerCase() === "green" ? "success" : "failed",
      );
    }

    if (historyOpenZoneIdRef.current === zoneId) {
      setHistoryRefetchTriggers((current) => ({
        ...current,
        [zoneId]: (current[zoneId] ?? 0) + 1,
      }));
    }
  }, [finish]);

  useSocketAck({ ackKey: "GatewayZoneSetACK", onAck: handleAck, assetCode: assetCode });

  const value = useMemo(
    () => ({ states, historyRefetchTriggers, start, fail, setHistoryOpenZoneId }),
    [states, historyRefetchTriggers, start, fail, setHistoryOpenZoneId],
  );

  return (
    <ZoneConfigurationAckContext.Provider value={value}>
      {children}
    </ZoneConfigurationAckContext.Provider>
  );
}

export function useZoneConfigurationAck() {
  const context = useContext(ZoneConfigurationAckContext);
  if (!context) {
    throw new Error("useZoneConfigurationAck must be used within ZoneConfigurationAckProvider");
  }
  return context;
}